import React, { useState } from 'react';
import { Field, Form, Formik } from 'formik';
import { useAppDispatch } from 'redux/store';
import { TextField } from 'formik-mui';
import { ButtonWithLoader } from 'components/Buttons';
import { useSelector } from 'react-redux';
import { authSelector, beginSMSLogin, completeSMSLogin } from 'redux/reducers/auth';
import useTheme from '@mui/material/styles/useTheme';
import {
    Typography,
    Button,
    Box,
    List,
    ListItem,
    ListItemButton,
    ListItemText,
    Chip,
    InputAdornment,
} from '@mui/material';
import * as Yup from 'yup';
import { PhoneLoginResponse, MatchingUser } from '../../api/Auth/userBeginLoginWithSMS';
import { formatPhoneDisplay, extractPhoneDigits } from '../../utils/phoneFormat';
import { useHistory, useLocation } from 'react-router-dom';
import { LocationState } from 'utils';
import { getNotificationCounts, getUserData } from '../../redux/reducers/user';
import OTPInput, { InputProps } from 'react-otp-input';
import userResendSMSCode from '../../api/Auth/userResendSMSCode';

interface LoginFormPhoneProps {
    onSwitchToUsername: () => void;
    initialPhone?: string;
}

// Validation schema for phone number
const phoneValidationSchema = Yup.object({
    phone: Yup.string()
        .required('Phone number is required')
        .test('phone-length', 'Phone number must be 10 digits', (value) => {
            if (!value) return false;
            const digits = extractPhoneDigits(value);
            return digits.length === 10;
        }),
});

// Validation schema for verification code
const verificationCodeSchema = Yup.object({
    code: Yup.string()
        .required('Verification code is required')
        .length(6, 'Verification code must be 6 digits'),
});

// Validation schema for unverified phone login
const unverifiedPhoneSchema = Yup.object({
    username: Yup.string().required('Username is required'),
    password: Yup.string().required('Password is required'),
    code: Yup.string()
        .required('Verification code is required')
        .length(6, 'Verification code must be 6 digits'),
});

const LoginFormPhone: React.FC<LoginFormPhoneProps> = ({ onSwitchToUsername, initialPhone }) => {
    const theme = useTheme();
    const dispatch = useAppDispatch();
    const { push } = useHistory();
    const location = useLocation<LocationState>();
    const { isLoading } = useSelector(authSelector);

    const [phoneVerificationStatus, setPhoneVerificationStatus] = useState<
        PhoneLoginResponse['status'] | null
    >(null);
    const [matchingUsers, setMatchingUsers] = useState<MatchingUser[]>([]);
    const [selectedUserUuid, setSelectedUserUuid] = useState<string>('');
    const [phoneNumber, setPhoneNumber] = useState<string>('');
    const [obscuredPhoneNumber, setObscuredPhoneNumber] = useState<string>('');
    const [resendLoading, setResendLoading] = useState(false);

    const handlePhoneSubmit = async (
        values: { phone: string },
        { setErrors, setFieldError }: any,
    ) => {
        const phoneDigits = extractPhoneDigits(values.phone);
        const formattedPhone = `+1${phoneDigits}`;

        const result = await dispatch(beginSMSLogin({ phone: formattedPhone }));

        if (beginSMSLogin.fulfilled.match(result)) {
            setPhoneVerificationStatus(result.payload.status);
            setMatchingUsers(result.payload.matching_users || []);
            setPhoneNumber(formattedPhone);
            setObscuredPhoneNumber(`(***) ***-${formattedPhone.replace(/\D/g, '').slice(-4)}`);
        } else if (beginSMSLogin.rejected.match(result)) {
            if (result.payload?.errors) {
                setErrors(result.payload.errors);
            } else {
                setFieldError(
                    'phone',
                    result.payload?.message || 'An error occurred. Please try again.',
                );
            }
        }
    };

    const handleSuccessfulLogin = () => {
        if (localStorage.getItem('athleteSubscribeUrl')) {
            const redirect = localStorage.getItem('athleteSubscribeUrl');
            localStorage.removeItem('athleteSubscribeUrl');
            window.location.href = redirect || '/home';
        } else if (location.state?.referrer) {
            push(location.state.referrer);
        } else {
            push('/home');
        }
        dispatch(getNotificationCounts());
        dispatch(getUserData());
    };

    const handleVerificationCodeSubmit = async (
        values: { code: string },
        { setErrors, setFieldError }: any,
    ) => {
        let result;

        if (phoneVerificationStatus === 'phone_verified_one_account' && matchingUsers.length > 0) {
            const user = matchingUsers[0];
            result = await dispatch(
                completeSMSLogin({
                    uuid: user.uuid,
                    phone: phoneNumber,
                    code: values.code,
                }),
            );
        } else if (
            phoneVerificationStatus === 'phone_verified_multiple_accounts' &&
            selectedUserUuid
        ) {
            result = await dispatch(
                completeSMSLogin({
                    uuid: selectedUserUuid,
                    phone: phoneNumber,
                    code: values.code,
                }),
            );
        }

        if (result && completeSMSLogin.fulfilled.match(result)) {
            handleSuccessfulLogin();
        } else if (result && completeSMSLogin.rejected.match(result)) {
            if (result.payload?.errors) {
                setErrors(result.payload.errors);
            } else {
                setFieldError('code', result.payload?.message || 'Invalid verification code.');
            }
        }
    };

    const handleUnverifiedPhoneSubmit = async (
        values: { username: string; password: string; code: string },
        { setErrors, setFieldError }: any,
    ) => {
        const result = await dispatch(
            completeSMSLogin({
                user_name: values.username,
                password: values.password,
                phone: phoneNumber,
                code: values.code,
            }),
        );

        if (completeSMSLogin.fulfilled.match(result)) {
            handleSuccessfulLogin();
        } else if (completeSMSLogin.rejected.match(result)) {
            if (result.payload?.errors) {
                setErrors(result.payload.errors);
            } else {
                setFieldError('code', result.payload?.message || 'Login failed.');
            }
        }
    };

    const handleResendCode = async () => {
        if (!phoneNumber) return;

        setResendLoading(true);
        try {
            await userResendSMSCode(phoneNumber);
            // You might want to show a success message here
        } catch (error) {
            console.error('Failed to resend code:', error);
            // You might want to show an error message here
        } finally {
            setResendLoading(false);
        }
    };

    const handleUserSelection = (userUuid: string) => {
        setSelectedUserUuid(userUuid);
    };

    const resetPhoneVerification = () => {
        setPhoneVerificationStatus(null);
        setMatchingUsers([]);
        setSelectedUserUuid('');
        setPhoneNumber('');
        setObscuredPhoneNumber('');
    };

    // Phone verified - single account
    if (phoneVerificationStatus === 'phone_verified_one_account') {
        return (
            <Box>
                <Typography variant="h6" gutterBottom align="center">
                    Verification Code Sent
                </Typography>
                <Typography
                    variant="body2"
                    color="textSecondary"
                    gutterBottom
                    align="center"
                    sx={{ mb: 3 }}
                >
                    We sent a code to {obscuredPhoneNumber}
                </Typography>

                <Formik
                    initialValues={{ code: '' }}
                    validationSchema={verificationCodeSchema}
                    onSubmit={handleVerificationCodeSubmit}
                >
                    {({ values, setFieldValue, isValid, dirty, errors, touched }) => (
                        <Form>
                            <Box sx={{ width: '100%' }}>
                                <OTPInput
                                    value={values.code}
                                    onChange={(value: string) => setFieldValue('code', value)}
                                    numInputs={6}
                                    inputType="tel"
                                    shouldAutoFocus={true}
                                    skipDefaultStyles={true}
                                    renderInput={(props: InputProps, index: number) => (
                                        <input
                                            {...props}
                                            disabled={isLoading}
                                            key={index}
                                            style={{
                                                width: '2.5rem',
                                                height: '2.5rem',
                                                margin: '0 2px',
                                                textAlign: 'center',
                                                fontSize: '1.5rem',
                                                fontWeight: 'bold',
                                                border: '1px solid #ccc',
                                                borderRadius: '6px',
                                                backgroundColor: 'white',
                                                color: 'black',
                                                outline: 'none',
                                            }}
                                            onFocus={(e) => {
                                                e.target.style.borderColor = 'black';
                                                e.target.style.borderWidth = '2px';
                                            }}
                                            onBlur={(e) => {
                                                e.target.style.borderColor = '#ccc';
                                                e.target.style.borderWidth = '1px';
                                            }}
                                        />
                                    )}
                                    containerStyle={{
                                        display: 'flex',
                                        justifyContent: 'center',
                                        marginBottom: '16px',
                                    }}
                                />
                                {errors.code && touched.code && (
                                    <Typography
                                        color="error"
                                        variant="body2"
                                        sx={{ mt: 1, textAlign: 'center' }}
                                    >
                                        {errors.code}
                                    </Typography>
                                )}
                            </Box>

                            <ButtonWithLoader
                                size="large"
                                type="submit"
                                color="primary"
                                variant="contained"
                                sx={{ width: '100%', marginBottom: theme.spacing(2) }}
                                disabled={isLoading ? true : !(isValid && dirty)}
                                loading={isLoading}
                            >
                                {isLoading ? 'Verifying...' : 'Verify Code'}
                            </ButtonWithLoader>
                        </Form>
                    )}
                </Formik>

                <Button
                    variant="text"
                    onClick={handleResendCode}
                    disabled={resendLoading}
                    sx={{ width: '100%', marginBottom: theme.spacing(2) }}
                >
                    {resendLoading ? 'Resending...' : 'Resend Code'}
                </Button>

                <Button
                    variant="text"
                    onClick={resetPhoneVerification}
                    sx={{ width: '100%', marginBottom: theme.spacing(2) }}
                >
                    Back to Phone Entry
                </Button>

                <Button variant="text" onClick={onSwitchToUsername} sx={{ width: '100%' }}>
                    Login with username instead
                </Button>
            </Box>
        );
    }

    // Phone verified - multiple accounts
    if (phoneVerificationStatus === 'phone_verified_multiple_accounts') {
        return (
            <Box style={{ width: '100%' }}>
                <Typography variant="h6" gutterBottom align="center">
                    Multiple Accounts Found
                </Typography>
                <Typography
                    variant="body2"
                    color="textSecondary"
                    gutterBottom
                    align="center"
                    sx={{ mb: 2 }}
                >
                    We sent a code to {obscuredPhoneNumber}. This phone is linked to multiple
                    usernames.
                </Typography>

                <Typography variant="body2" sx={{ mb: 2, textAlign: 'center' }}>
                    Select one of the {matchingUsers.length} accounts below
                </Typography>

                <List
                    sx={{
                        maxHeight: 170,
                        minHeight: 85,
                        overflowY: 'auto',
                        mb: 2,
                        borderColor: 'divider',
                        borderRadius: 1,
                    }}
                >
                    {matchingUsers.map((user, index) => (
                        <ListItem key={user.uuid} disablePadding>
                            <ListItemButton
                                selected={selectedUserUuid === user.uuid}
                                onClick={() => handleUserSelection(user.uuid)}
                                sx={{
                                    border: `2px solid ${theme.palette.primary.main}`,
                                    marginBottom: '8px',
                                    borderRadius: '8px',
                                    '&.Mui-selected': {
                                        bgcolor: 'lightgray',
                                        color: 'primary.main',
                                    },
                                }}
                            >
                                <ListItemText
                                    primary={
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <Typography>
                                                {`${user.first_name} ${user.last_name}`}
                                            </Typography>
                                            {index === 0 && (
                                                <Chip
                                                    label="last used"
                                                    size="small"
                                                    sx={{
                                                        borderRadius: '4px',
                                                        fontSize: '0.7rem',
                                                        height: '18px',
                                                        '& .MuiChip-label': {
                                                            px: 1,
                                                        },
                                                    }}
                                                />
                                            )}
                                        </Box>
                                    }
                                    secondary={user.user_name}
                                />
                            </ListItemButton>
                        </ListItem>
                    ))}
                </List>

                <Formik
                    initialValues={{ code: '' }}
                    validationSchema={verificationCodeSchema}
                    onSubmit={handleVerificationCodeSubmit}
                >
                    {({ values, setFieldValue, isValid, dirty, errors, touched }) => (
                        <Form>
                            <Box>
                                <OTPInput
                                    value={values.code}
                                    onChange={(value: string) => setFieldValue('code', value)}
                                    numInputs={6}
                                    inputType="tel"
                                    shouldAutoFocus={true}
                                    skipDefaultStyles={true}
                                    renderInput={(props: InputProps, index: number) => (
                                        <input
                                            {...props}
                                            disabled={isLoading}
                                            key={index}
                                            style={{
                                                width: '2.5rem',
                                                height: '2.5rem',
                                                margin: '0 2px',
                                                textAlign: 'center',
                                                fontSize: '1.5rem',
                                                fontWeight: 'bold',
                                                border: '1px solid #ccc',
                                                borderRadius: '6px',
                                                backgroundColor: 'white',
                                                color: 'black',
                                                outline: 'none',
                                            }}
                                            onFocus={(e) => {
                                                e.target.style.borderColor = 'black';
                                                e.target.style.borderWidth = '2px';
                                            }}
                                            onBlur={(e) => {
                                                e.target.style.borderColor = '#ccc';
                                                e.target.style.borderWidth = '1px';
                                            }}
                                        />
                                    )}
                                    containerStyle={{
                                        display: 'flex',
                                        justifyContent: 'center',
                                        marginBottom: '16px',
                                    }}
                                />
                                {errors.code && touched.code && (
                                    <Typography
                                        color="error"
                                        variant="body2"
                                        sx={{ mt: 1, textAlign: 'center' }}
                                    >
                                        {errors.code}
                                    </Typography>
                                )}
                            </Box>

                            <ButtonWithLoader
                                size="large"
                                type="submit"
                                color="primary"
                                variant="contained"
                                sx={{ width: '100%', marginBottom: theme.spacing(2) }}
                                disabled={
                                    isLoading || !selectedUserUuid ? true : !(isValid && dirty)
                                }
                                loading={isLoading}
                            >
                                {isLoading ? 'Verifying...' : 'Verify Code'}
                            </ButtonWithLoader>
                        </Form>
                    )}
                </Formik>

                <Button
                    variant="text"
                    onClick={handleResendCode}
                    disabled={resendLoading}
                    sx={{ width: '100%', marginBottom: theme.spacing(2) }}
                >
                    {resendLoading ? 'Resending...' : 'Resend Code'}
                </Button>

                <Button
                    variant="text"
                    onClick={resetPhoneVerification}
                    sx={{ width: '100%', marginBottom: theme.spacing(2) }}
                >
                    Back to Phone Entry
                </Button>

                <Button variant="text" onClick={onSwitchToUsername} sx={{ width: '100%' }}>
                    Login with username instead
                </Button>
            </Box>
        );
    }

    // Phone unverified
    if (phoneVerificationStatus === 'phone_unverified') {
        return (
            <Box>
                <Typography variant="h6" gutterBottom align="center">
                    Phone Verification Required
                </Typography>
                <Typography
                    variant="body2"
                    color="textSecondary"
                    gutterBottom
                    align="center"
                    sx={{ mb: 3 }}
                >
                    Enter your credentials and verification code sent to {obscuredPhoneNumber}
                </Typography>

                <Formik
                    initialValues={{ username: '', password: '', code: '' }}
                    validationSchema={unverifiedPhoneSchema}
                    onSubmit={handleUnverifiedPhoneSubmit}
                >
                    {({ values, setFieldValue, isValid, dirty, errors, touched }) => (
                        <Form>
                            <Field
                                sx={{ width: '100%', marginBottom: theme.spacing(2) }}
                                component={TextField}
                                variant="filled"
                                name="username"
                                label="Username"
                            />

                            <Field
                                sx={{ width: '100%', marginBottom: theme.spacing(2) }}
                                component={TextField}
                                variant="filled"
                                name="password"
                                type="password"
                                label="Password"
                            />

                            <Box sx={{ width: '100%' }}>
                                <OTPInput
                                    value={values.code}
                                    onChange={(value: string) => setFieldValue('code', value)}
                                    numInputs={6}
                                    inputType="tel"
                                    shouldAutoFocus={true}
                                    skipDefaultStyles={true}
                                    renderInput={(props: InputProps, index: number) => (
                                        <input
                                            {...props}
                                            disabled={isLoading}
                                            key={index}
                                            style={{
                                                width: '2.5rem',
                                                height: '2.5rem',
                                                margin: '0 2px',
                                                textAlign: 'center',
                                                fontSize: '1.5rem',
                                                fontWeight: 'bold',
                                                border: '1px solid #ccc',
                                                borderRadius: '6px',
                                                backgroundColor: 'white',
                                                color: 'black',
                                                outline: 'none',
                                            }}
                                            onFocus={(e) => {
                                                e.target.style.borderColor = 'black';
                                                e.target.style.borderWidth = '2px';
                                            }}
                                            onBlur={(e) => {
                                                e.target.style.borderColor = '#ccc';
                                                e.target.style.borderWidth = '1px';
                                            }}
                                        />
                                    )}
                                    containerStyle={{
                                        display: 'flex',
                                        justifyContent: 'center',
                                        marginBottom: '16px',
                                    }}
                                />
                                {errors.code && touched.code && (
                                    <Typography
                                        color="error"
                                        variant="body2"
                                        sx={{ mt: 1, textAlign: 'center' }}
                                    >
                                        {errors.code}
                                    </Typography>
                                )}
                            </Box>

                            <ButtonWithLoader
                                size="large"
                                type="submit"
                                color="primary"
                                variant="contained"
                                sx={{ width: '100%', marginBottom: theme.spacing(2) }}
                                disabled={isLoading ? true : !(isValid && dirty)}
                                loading={isLoading}
                            >
                                {isLoading ? 'Verifying...' : 'Verify & Login'}
                            </ButtonWithLoader>
                        </Form>
                    )}
                </Formik>

                <Button
                    variant="text"
                    onClick={handleResendCode}
                    disabled={resendLoading}
                    sx={{ width: '100%', marginBottom: theme.spacing(2) }}
                >
                    {resendLoading ? 'Resending...' : 'Resend Code'}
                </Button>

                <Button
                    variant="text"
                    onClick={resetPhoneVerification}
                    sx={{ width: '100%', marginBottom: theme.spacing(2) }}
                >
                    Back to Phone Entry
                </Button>

                <Button variant="text" onClick={onSwitchToUsername} sx={{ width: '100%' }}>
                    Login with username instead
                </Button>
            </Box>
        );
    }

    // Phone not found
    if (phoneVerificationStatus === 'phone_not_found') {
        return (
            <Box>
                <Typography variant="h6" gutterBottom align="center">
                    Phone Number Not Found
                </Typography>
                <Typography
                    variant="body2"
                    color="textSecondary"
                    gutterBottom
                    align="center"
                    sx={{ mb: 3, color: 'red', fontWeight: 'bold' }}
                >
                    No account is associated with this phone number.
                </Typography>

                <Button
                    variant="contained"
                    onClick={() => push('/register')}
                    sx={{
                        width: '100%',
                        marginBottom: theme.spacing(2),
                    }}
                >
                    Create Account
                </Button>

                <Button
                    variant="outlined"
                    onClick={resetPhoneVerification}
                    sx={{
                        width: '100%',
                        marginBottom: theme.spacing(2),
                    }}
                >
                    Try Different Phone
                </Button>

                <Button variant="text" onClick={onSwitchToUsername} sx={{ width: '100%' }}>
                    Login with username instead
                </Button>
            </Box>
        );
    }

    const getInitialPhoneDisplay = (): string => {
        if (!initialPhone) return '';
        const digits = initialPhone.replace(/\D/g, '');
        const last10 = digits.length > 10 ? digits.slice(-10) : digits;
        return formatPhoneDisplay(last10);
    };

    // Default phone input form
    return (
        <Formik
            initialValues={{ phone: getInitialPhoneDisplay() }}
            validationSchema={phoneValidationSchema}
            onSubmit={handlePhoneSubmit}
        >
            {({ values, setFieldValue, isValid, dirty }) => {
                const hasInitialPhone = !!initialPhone;
                const isButtonDisabled = isLoading
                    ? true
                    : hasInitialPhone
                    ? !isValid
                    : !(isValid && dirty);

                return (
                    <Form>
                        <Field
                            sx={{ width: '100%', marginBottom: theme.spacing(4) }}
                            component={TextField}
                            variant="filled"
                            name="phone"
                            type="tel"
                            label="Phone Number"
                            placeholder="(555) 555-5555"
                            value={values.phone}
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                const formattedValue = formatPhoneDisplay(e.target.value);
                                void setFieldValue('phone', formattedValue);
                            }}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">+1</InputAdornment>
                                ),
                            }}
                        />

                        <ButtonWithLoader
                            size="large"
                            type="submit"
                            color="primary"
                            variant="contained"
                            sx={{ width: '100%', marginBottom: theme.spacing(4) }}
                            disabled={isButtonDisabled}
                            loading={isLoading}
                        >
                            {isLoading ? 'Sending Code...' : 'Send Verification Code'}
                        </ButtonWithLoader>

                        <Button variant="text" onClick={onSwitchToUsername} sx={{ width: '100%' }}>
                            Login with username instead
                        </Button>
                    </Form>
                );
            }}
        </Formik>
    );
};

export default LoginFormPhone;
